import React, { useCallback, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { FormattedMessage, useIntl } from 'react-intl';

import { RequiredLabel } from '../../RequiredLabel';
import { FieldLabel, FormField, getFieldSurfaceStyles } from '../FormField';

export interface DateTimeFieldProps {
  clampLabel?: boolean;
  disabled?: boolean;
  /** Shows the time picker and the exact date-time tooltip. */
  enableTime?: boolean;
  id?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  label?: React.ReactNode;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange: (value: DateTime | null) => void;
  required?: boolean;
  value?: DateTime | null;
}

const labelRowStyles = { alignItems: 'center' };
const pickerRowStyles = { alignItems: 'center' };
const datePickerStyles = { flex: 1 };
const fieldStyles = getFieldSurfaceStyles('sunken');
const infoButtonStyles = { p: 0.25, ml: 0.5, color: 'text.secondary' };
const timeButtonStyles = { p: 0.5, color: 'text.secondary' };
const timeLabelStyles = {
  minWidth: 40,
  fontSize: '0.75rem',
  color: 'text.secondary',
};
const popoverStyles = { p: 2 };

/** Matches "09.10.2021" and the Czech "9. 10. 2021" once spaces are removed. */
const PASTED_DATE_FORMAT = 'd.M.yyyy';

const parsePastedDate = (text: string) => {
  const date = DateTime.fromFormat(
    text.replace(/\s+/g, ''),
    PASTED_DATE_FORMAT
  );

  return date.isValid ? date : null;
};

const formatExactValue = (value: DateTime | null | undefined, empty: string) =>
  value?.isValid ? value.toFormat('dd.MM.yyyy HH:mm:ss') : empty;

const hasTime = (value: DateTime | null | undefined) =>
  Boolean(
    value?.isValid &&
      (value.hour !== 0 || value.minute !== 0 || value.second !== 0)
  );

const DateTimeField = React.memo(
  ({
    clampLabel = false,
    disabled = false,
    enableTime = true,
    id = 'date-time-field',
    inputRef,
    label,
    name,
    onBlur,
    onChange,
    required = false,
    value,
  }: DateTimeFieldProps) => {
    const intl = useIntl();
    const [timeAnchor, setTimeAnchor] = useState<HTMLElement | null>(null);
    const emptyLabel = intl.formatMessage({
      id: 'find.field.noDateSet',
      defaultMessage: 'No date set',
    });
    const setTimeLabel = intl.formatMessage({
      id: 'find.field.setTime',
      defaultMessage: 'Set time',
    });
    const tooltip = formatExactValue(value, emptyLabel);
    const dateSlotProps = useMemo(
      () => ({
        textField: {
          id,
          inputRef,
          name,
          onBlur,
          size: 'small' as const,
          sx: fieldStyles,
        },
      }),
      [id, inputRef, name, onBlur]
    );
    const timeSlotProps = useMemo(
      () => ({
        textField: {
          size: 'small' as const,
          sx: fieldStyles,
        },
      }),
      []
    );
    const handleDateChange = useCallback(
      (next: DateTime | null) => {
        onChange(next?.isValid ? next.startOf('day') : null);
      },
      [onChange]
    );
    // MUI only parses its locale display format and would clear the value, so
    // a recognised date is handled before the paste reaches the input.
    const handlePasteCapture = useCallback(
      (event: React.ClipboardEvent) => {
        const pastedDate = parsePastedDate(event.clipboardData.getData('text'));

        if (!pastedDate) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        handleDateChange(pastedDate);
      },
      [handleDateChange]
    );
    const handleTimeChange = useCallback(
      (next: DateTime | null) => {
        if (!next?.isValid) {
          return;
        }

        const base = value?.isValid ? value : DateTime.now().startOf('day');
        onChange(
          base.set({
            hour: next.hour,
            minute: next.minute,
            second: 0,
            millisecond: 0,
          })
        );
      },
      [onChange, value]
    );
    const handleOpenTime = useCallback(
      (event: React.MouseEvent<HTMLElement>) =>
        setTimeAnchor(event.currentTarget),
      []
    );
    const handleCloseTime = useCallback(() => setTimeAnchor(null), []);

    return (
      <FormField clampLabel={clampLabel}>
        <Stack direction="row" sx={labelRowStyles}>
          <FieldLabel htmlFor={id}>
            {label || (
              <FormattedMessage
                id="find.field.dateFound"
                defaultMessage="Date found"
              />
            )}{' '}
            {required && <RequiredLabel />}
          </FieldLabel>
          {enableTime && (
            <Tooltip arrow title={tooltip} placement="top">
              <IconButton
                size="small"
                aria-label={intl.formatMessage({
                  id: 'find.field.showExactDateTime',
                  defaultMessage: 'Show exact date and time found',
                })}
                sx={infoButtonStyles}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Stack
          direction="row"
          spacing={0.5}
          sx={pickerRowStyles}
          onPasteCapture={handlePasteCapture}
        >
          <DatePicker
            disabled={disabled}
            value={value ?? null}
            onChange={handleDateChange}
            slotProps={dateSlotProps}
            sx={datePickerStyles}
          />
          {enableTime && (
            <Tooltip title={setTimeLabel}>
              <IconButton
                disabled={disabled}
                size="small"
                aria-label={setTimeLabel}
                onClick={handleOpenTime}
                sx={timeButtonStyles}
              >
                <AccessTimeIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          {enableTime && hasTime(value) && (
            <Typography sx={timeLabelStyles}>
              {value?.toFormat('HH:mm')}
            </Typography>
          )}
        </Stack>
        <Popover
          open={Boolean(timeAnchor)}
          anchorEl={timeAnchor}
          onClose={handleCloseTime}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Stack sx={popoverStyles} spacing={1}>
            <TimePicker
              label={intl.formatMessage({
                id: 'find.field.timeFound',
                defaultMessage: 'Time found',
              })}
              ampm={false}
              views={['hours', 'minutes']}
              value={value ?? DateTime.now().startOf('day')}
              onChange={handleTimeChange}
              slotProps={timeSlotProps}
            />
          </Stack>
        </Popover>
      </FormField>
    );
  }
);

DateTimeField.displayName = 'DateTimeField';

export { DateTimeField };
